/**
 * Helpers de presentación del dominio Dashboard.
 *
 * Puro formato/traducción para la UI; sin lógica de negocio ni acceso a la API
 * (`docs/frontend_architecture.md`, Pr. 6). Cubre la etiqueta legible del rol
 * (réplica del panel "Información del Sistema" del SSR) y el formateo de la marca
 * de tiempo de última actualización.
 *
 * El diccionario de roles es PROPIO del dominio para no acoplarse a
 * `user_activity` (cuyo `ROLE_LABELS.admin === "Admin"`): aquí el SSR mostraba
 * "Administrador", así que se replica ese texto exacto.
 */

/**
 * Etiquetas legibles de los roles, tal como las pintaba el panel "Información del
 * Sistema" del SSR (badge por rol). Un rol no listado cae a su id capitalizado.
 */
export const ROLE_LABELS: Record<string, string> = {
  admin: "Administrador",
  supervisor: "Supervisor",
  operario: "Operario",
  entregas: "Entregas",
  mostrador: "Mostrador",
  caja: "Caja",
  logistica: "Logística",
};

/**
 * Etiqueta legible de un rol. Usa el diccionario del dominio; si el rol no está
 * mapeado, capitaliza su id crudo (paridad con el `{{ user.role.title() }}` del
 * SSR). Sin rol, devuelve "—".
 */
export function roleLabel(role?: string): string {
  if (!role) {
    return "—";
  }
  return ROLE_LABELS[role] ?? role.charAt(0).toUpperCase() + role.slice(1);
}

/**
 * Formatea la marca de tiempo (epoch ms de `dataUpdatedAt` de TanStack Query) al
 * formato del SSR `dd/MM/yyyy hh:mm AM/PM` (12h, AM/PM en MAYÚSCULAS).
 *
 * Se fija la zona horaria a `America/Bogota` (UTC-5) para igualar la hora local
 * de Colombia que usaba el SSR y hacer el resultado determinista (independiente
 * de la zona del navegador o del runner de tests). Se usa `formatToParts` porque
 * el locale es-CO inserta una coma tras el año y emite el periodo como "p. m."
 * (con puntos y espacios); aquí se reensambla sin la coma y con el periodo
 * normalizado a "AM"/"PM" en mayúsculas, clavando el formato del SSR.
 *
 * Si `ts` es 0 (aún no hay datos), devuelve "—".
 */
export function formatUpdatedAt(ts: number): string {
  if (!ts) {
    return "—";
  }
  const parts = new Intl.DateTimeFormat("es-CO", {
    timeZone: "America/Bogota",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(new Date(ts));

  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? "";
  // es-CO emite el periodo como "p. m."/"a. m."; lo normalizamos a "PM"/"AM".
  const period = get("dayPeriod").replace(/[.\s]/g, "").toUpperCase();

  return `${get("day")}/${get("month")}/${get("year")} ${get("hour")}:${get("minute")} ${period}`;
}
